import { ConnectError } from '@bufbuild/connect';
import axios, { AxiosRequestConfig } from 'axios';
import constate from 'constate';
import { debounce } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { FileWithPath } from 'react-dropzone';
import { useIntl } from 'react-intl';

import { AssetType } from '@/shared/api/protocol-ts/model/dto_asset_pb';
import { BASE_URI } from '@/shared/config';
import {
  useAppDispatch,
  useAppSelector,
  useConfirmRefresh,
} from '@/shared/hooks';
import { debug } from '@/shared/lib';

import { organizationModel } from '@/entities/organization';
import { studyModel } from '@/entities/study';

import {
  divideTargetsIntoChunks,
  filterArchivesIfDicomPresent,
  getStudyRequestParams,
  isInvalidFileError,
  isSuccessfulUploadStatus,
  resolveCbctUploadOrder,
} from '../lib';
import { assetStatusErrorMessages } from '../model';

type UploadStatus = 'idle' | 'uploading' | 'uploaded' | 'cancelled' | 'failed';

export type UploadFile = {
  id: string;
  file: FileWithPath;
  status: UploadStatus;
  progress?: number;
  rate?: number;
  loaded?: number;
  errorMessage?: string;
};

type UploadFileState = Record<string, UploadFile>;

interface RetryConfig extends AxiosRequestConfig {
  retry: number;
  retryDelay: number;
}

const globalConfig: RetryConfig = {
  retry: 3,
  retryDelay: 1000,
};

const uploadAxios = axios.create();

const isCanceledError = (error: unknown): boolean =>
  // Official axios cancel check
  axios.isCancel(error) ||
  // axios v1+ error code set on CanceledError instances
  (error as { code?: string })?.code === 'ERR_CANCELED' ||
  // axios v1+ error class name fallback
  (error as { name?: string })?.name === 'CanceledError';

uploadAxios.interceptors.response.use(undefined, (error) => {
  const { config, message } = error;

  if (!config || !config.retry || isCanceledError(error)) {
    return Promise.reject(error);
  }
  // retry while Network timeout or Network Error
  if (!(message.includes('timeout') || message.includes('Network Error'))) {
    return Promise.reject(error);
  }

  config.retry -= 1;

  const delayRetryRequest = new Promise<void>((resolve) => {
    setTimeout(() => {
      debug('retry the request', config.url);
      resolve();
    }, config.retryDelay || 1000);
  });

  return delayRetryRequest.then(() => uploadAxios(config));
});

export const getFormattedPercent = (part: number, total: number) =>
  Math.floor((part / total) * 100);

export type UploadSessionPullStatus =
  | 'failed'
  | 'uploading'
  | 'success'
  | 'unsuccessful';

export type UploadSessionPull = {
  files: Record<string, UploadFile>;
  patientID: string;
  sessionID: string;
  StudyID?: string;
  status: UploadSessionPullStatus;
  meta?: Record<string, unknown>;
  errorMessage?: string;
  startAt?: number;
};

export type UploadAssetParams = {
  patientID?: string;
  userID?: string;
  staffID?: string;
  files: FileWithPath[];
  assetType: AssetType;
  rawMeshViewer?: boolean;
  toothID?: string;
  albumID?: string;
  meta?: Record<string, unknown>;
};

const useUploadAsset = () => {
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const [canceledSessions, setCanceledSessions] = useState<string[]>([]);

  const [sessionsAbortControllers, setSessionsAbortControllers] = useState<
    Record<string, AbortController[]>
  >({});

  const [uploadSessionPullStack, setUploadSessionPullStack] = useState<
    Record<string, UploadSessionPull>
  >({});

  const { formatMessage } = useIntl();

  const handleChangeCanceledSessionID = useCallback(
    (canceledSessionID: string) => {
      sessionsAbortControllers[canceledSessionID]?.forEach((controller) =>
        controller.abort(),
      );
      setCanceledSessions((prev) => [...prev, canceledSessionID]);
    },
    [sessionsAbortControllers],
  );

  const dispatch = useAppDispatch();

  const { addConfirmRefreshListener, removeConfirmRefreshListener } =
    useConfirmRefresh();

  const organizationID = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );

  useEffect(() => {
    const someUploadSessionInProgress = Object.values(
      uploadSessionPullStack,
    ).some((pullStack) => pullStack.status === 'uploading');

    if (someUploadSessionInProgress) {
      addConfirmRefreshListener();
    } else {
      removeConfirmRefreshListener();
    }

    return () => {
      removeConfirmRefreshListener();
    };
  }, [
    addConfirmRefreshListener,
    removeConfirmRefreshListener,
    uploadSessionPullStack,
    dispatch,
    formatMessage,
  ]);

  const handleChangeUploadSessionPullStack = (
    updatedPullStack: Record<string, UploadSessionPull>,
  ) => {
    setUploadSessionPullStack(updatedPullStack);
  };

  const startUploadAsset = useCallback(
    async (
      {
        patientID,
        files: inputFiles,
        assetType,
        rawMeshViewer,
        meta,
        userID,
        staffID,
        toothID,
        albumID,
      }: UploadAssetParams,
      hideProgress?: boolean,
    ) => {
      // NOTE: WA-6247 — drop archives if the drop already contains plain DICOM
      // files, so the backend ingest pipeline does not unpack and re-ingest them.
      const filteredFiles = await filterArchivesIfDicomPresent(inputFiles);

      // Enter the uploading state before CBCT validation: it can run for up to
      // VALIDATION_TIMEOUT_MS and must keep the progress UI open, otherwise the
      // modal would close with no pending state visible during this phase.
      if (!hideProgress) {
        setIsUploading(true);
      }

      // CBCT: validate the DICOM series in the browser and derive its axial
      // slice order so the server can stream the DICOM->DCCT conversion. Any
      // failure falls back to the original order (ordered=false) with a debug
      // message the server persists for diagnostics.
      const {
        files,
        ordered: cbctOrdered,
        validationError: cbctValidationError,
      } = await resolveCbctUploadOrder({ assetType, files: filteredFiles });

      let sessionID: string = '';

      try {
        const { UploadSession, UploadTargets, Study, AssetID } = await dispatch(
          studyModel.thunks.startUploadSession({
            ...getStudyRequestParams({
              patientID,
              assetType,
              organizationID,
              files,
              rawMeshViewer,
              userID,
              staffID,
              toothID,
              albumID,
              ordered: cbctOrdered,
              validationError: cbctValidationError,
            }),
          }),
        ).unwrap();

        sessionID = UploadSession?.ID ?? '';

        const uploadFiles = files.reduce((acc, currentFile) => {
          if (currentFile.path) {
            acc[currentFile.path] = {
              id: currentFile.path,
              file: currentFile,
              status: 'idle',
            };
          }

          return acc;
        }, {} as UploadFileState);

        if (sessionID && patientID) {
          setUploadSessionPullStack((currentPull) => ({
            ...currentPull,
            [sessionID]: {
              sessionID,
              patientID,
              files: uploadFiles,
              status: 'uploading',
              StudyID: UploadSession?.Target?.StudyID,
              meta,
            },
          }));
        }

        const startAt = Date.now();

        setUploadSessionPullStack((prev) => ({
          ...prev,
          [sessionID]: {
            ...prev[sessionID],
            startAt,
          },
        }));

        const dividedTargets = divideTargetsIntoChunks(UploadTargets);

        // upload files one by one

        for await (const uploadTargets of dividedTargets) {
          await Promise.all(
            uploadTargets.map(async (uploadTarget) => {
              const { UploadURL, Path, FileID } = uploadTarget;
              const file =
                files.length > 1
                  ? files.find(({ path }) => path === Path)
                  : files.at(0);

              const uploadFile = uploadFiles[Path];

              // Each 15 seconds notify about upload progress
              const notifyFileUploadProgress = () => {
                dispatch(
                  studyModel.thunks.notifyFileUploadProgress({
                    FileID,
                  }),
                );
              };

              const debouncedNotifyFileUploadProgress = debounce(
                notifyFileUploadProgress,
                15000,
                { maxWait: 15000 },
              );

              setUploadSessionPullStack((prev) => ({
                ...prev,
                [sessionID]: {
                  ...prev[sessionID],
                  files: {
                    ...(prev[sessionID]?.files ? prev[sessionID].files : {}),
                    [Path]: {
                      ...uploadFile,
                      progress: 0,
                      status: 'uploading',
                    },
                  },
                },
              }));

              if (file === undefined) {
                // TODO: [2|m] no hardcoded messages
                throw new Error('There is no file with such path');
              }

              await dispatch(
                studyModel.thunks.notifyFileUploadStarted({ FileID }),
              );

              const controller = new AbortController();

              setSessionsAbortControllers((prev) => ({
                ...prev,
                [sessionID]: [...(prev[sessionID] ?? []), controller],
              }));

              return uploadAxios
                .put(UploadURL, file, {
                  ...globalConfig,
                  baseURL: BASE_URI,
                  headers: {
                    'Content-Type': file.type || 'application/octet-stream',
                    'Content-Disposition': `name="${encodeURIComponent(file.name)}"`,
                  },
                  signal: controller.signal,
                  withCredentials: false,
                  onUploadProgress: (event) => {
                    debouncedNotifyFileUploadProgress();

                    setUploadSessionPullStack((prev) => ({
                      ...prev,
                      [sessionID]: {
                        ...prev[sessionID],
                        files: {
                          ...(prev[sessionID]?.files
                            ? prev[sessionID].files
                            : {}),
                          [Path]: {
                            ...prev[sessionID]?.files?.[Path],
                            progress: event.progress,
                            status: 'uploading',
                            rate: event.rate,
                            loaded: event.loaded,
                          },
                        },
                      },
                    }));
                  },
                })
                .then(async (response) => {
                  // update successful uploaded file status
                  if (isSuccessfulUploadStatus(response.status)) {
                    await dispatch(
                      studyModel.thunks.notifyFileUploadFinished({ FileID }),
                    );

                    setUploadSessionPullStack((prev) => ({
                      ...prev,
                      [sessionID]: {
                        ...prev[sessionID],
                        files: {
                          ...(prev[sessionID]?.files
                            ? prev[sessionID].files
                            : {}),
                          [Path]: {
                            ...prev[sessionID]?.files?.[Path],
                            progress: 1,
                            status: 'uploaded',
                          },
                        },
                      },
                    }));
                  }

                  return response;
                })
                .catch(async (error) => {
                  // update failed uploaded file status
                  setUploadSessionPullStack((prev) => ({
                    ...prev,
                    [sessionID]: {
                      ...prev[sessionID],
                      files: {
                        ...(prev[sessionID]?.files
                          ? prev[sessionID].files
                          : {}),
                        [Path]: {
                          ...prev[sessionID]?.files?.[Path],
                          status: 'failed',
                          errorMessage: error.message,
                        },
                      },
                    },
                  }));

                  // NOTE: Do not spread the error into a plain object — spreading an Error loses
                  // its prototype chain, so ConnectError.from() can no longer detect it as an
                  // Error instance and falls back to String(reason), producing "[unknown] [object Object]".
                  // Attach FileID directly to preserve instanceof checks.
                  (error as Error & { FileID: string }).FileID = FileID;
                  throw error;
                })
                .finally(() => {
                  debouncedNotifyFileUploadProgress.cancel();
                });
            }),
          );
        }

        // If all files uploaded successfully close session
        await dispatch(
          studyModel.thunks.closeSession({ UploadSessionID: sessionID }),
        );

        // update session success status
        setUploadSessionPullStack((prev) => ({
          ...prev,
          [sessionID]: {
            ...prev[sessionID],
            progress: 1,
            status: 'success',
          },
        }));

        setSessionsAbortControllers((prev) => {
          const next = { ...prev };
          delete next[sessionID];
          return next;
        });

        return {
          studyID: Study?.ID,
          assetID: AssetID || undefined,
          fileID: UploadTargets[0]?.FileID,
        };
      } catch (error) {
        const connectError = ConnectError.from(error);

        if (sessionID) {
          await dispatch(
            studyModel.thunks.failSession({
              UploadSessionID: sessionID,
              Reason: {
                case: 'Error',
                value: {
                  FileID:
                    (connectError.cause as Error & { FileID: string })
                      ?.FileID ?? '',
                  Message: connectError?.message,
                },
              },
            }),
          );

          const isInvalidError = isInvalidFileError(connectError);

          // update session failed status
          setUploadSessionPullStack((prev) => ({
            ...prev,
            [sessionID]: {
              ...prev[sessionID],
              errorMessage: isInvalidError
                ? formatMessage(assetStatusErrorMessages.unsuccessful)
                : formatMessage(assetStatusErrorMessages.failed),
              progress: 1,
              status: isInvalidError ? 'unsuccessful' : 'failed',
            },
          }));

          setSessionsAbortControllers((prev) => {
            const next = { ...prev };
            delete next[sessionID];
            return next;
          });
        }

        throw connectError;
      }
    },
    [dispatch, organizationID, formatMessage],
  );

  return {
    uploadSessionPullStack,
    canceledSessions,
    startUploadAsset,
    handleChangeUploadSessionPullStack,
    handleChangeCanceledSessionID,
    isUploading,
  };
};

const [UploadAssetProvider, useUploadAssetContext] = constate(useUploadAsset);

export { UploadAssetProvider, useUploadAssetContext };
